import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  X,
  Calculator,
  AlertCircle,
  Sparkles,
  Scale,
  Gem,
  Receipt,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  RotateCcw,
  Loader2,
  TrendingUp,
  TrendingDown,
  Clock,
  MapPin,
  Tag,
  FileText,
  Zap,
  Building2,
} from "lucide-react";
import { calculatePricingThunk } from "../../../store/thunks/pricingThunks";
import {
  selectCalculatedPricing,
  selectCalculationLoading,
  selectCalculationError,
} from "../../../store/slices/pricingSlice";
import { getBranchesThunk } from "../../../store/thunks/branchThunks";

// ============================================
// CUSTOM SELECT COMPONENT
// ============================================
const CustomSelect = ({ label, name, value, onChange, options, required }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find((opt) => opt.value === value);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-xs font-medium text-neutral-dark uppercase tracking-wide mb-2">
          {label} {required && <span className="text-secondary">*</span>}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between px-4 py-3 
          bg-white border-2 rounded-xl text-left
          transition-all duration-200 ease-out
          ${
            isOpen
              ? "border-primary shadow-[0_0_0_3px_rgba(22,101,52,0.1)]"
              : "border-neutral-light hover:border-primary/30"
          }
        `}
      >
        <span
          className={`text-sm ${
            selectedOption ? "text-text font-medium" : "text-neutral-dark/60"
          }`}
        >
          {selectedOption?.label || "Select..."}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-neutral-dark transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        className={`
          absolute z-50 w-full mt-2 bg-white border border-neutral-light 
          rounded-xl shadow-lg py-1.5 max-h-52 overflow-auto
          transition-all duration-200 ease-out origin-top
          ${
            isOpen
              ? "opacity-100 scale-100 translate-y-0"
              : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
          }
        `}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              onChange({ target: { name, value: option.value } });
              setIsOpen(false);
            }}
            className={`
              w-full px-4 py-2.5 text-left text-sm transition-colors
              ${
                value === option.value
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-text hover:bg-neutral-light"
              }
            `}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================
// INPUT FIELD COMPONENT
// ============================================
const InputField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  required,
  suffix,
  step,
  min,
}) => (
  <div>
    {label && (
      <label className="block text-xs font-medium text-neutral-dark uppercase tracking-wide mb-2">
        {label} {required && <span className="text-secondary">*</span>}
      </label>
    )}
    <div className="relative group">
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        step={step}
        min={min}
        placeholder={placeholder}
        className={`
          w-full px-4 py-3 text-sm font-medium text-text
          bg-white border-2 border-neutral-light rounded-xl
          placeholder:text-neutral-dark/40 placeholder:font-normal
          transition-all duration-200 ease-out
          focus:border-primary focus:shadow-[0_0_0_3px_rgba(22,101,52,0.1)] focus:outline-none
          hover:border-primary/30
          ${suffix ? "pr-12" : ""}
        `}
      />
      {suffix && (
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-neutral-dark uppercase">
          {suffix}
        </span>
      )}
    </div>
  </div>
);

// ============================================
// SECTION DIVIDER COMPONENT
// ============================================
const SectionDivider = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-3 py-4">
    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center shadow-sm border border-primary/10">
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <span className="text-sm font-semibold text-text">{title}</span>
    <div className="flex-1 h-px bg-gradient-to-r from-primary/20 to-transparent" />
  </div>
);

// ============================================
// PRICE ROW COMPONENT
// ============================================
const PriceRow = ({ label, value, sublabel, highlight, large, indent }) => (
  <div
    className={`
      flex items-center justify-between py-3 
      ${
        highlight
          ? "bg-gradient-to-r from-secondary/10 via-secondary/5 to-primary/5 -mx-4 px-4 rounded-xl border border-secondary/20"
          : "border-b border-neutral-light/50 last:border-0"
      }
      ${indent ? "pl-4 opacity-80" : ""}
    `}
  >
    <div className="flex-1 min-w-0">
      <span
        className={`
        ${highlight ? "font-bold text-primary-dark" : ""}
        ${large ? "font-semibold text-text" : ""}
        ${!highlight && !large ? "text-neutral-dark" : ""}
        ${indent ? "text-sm" : "text-sm"}
      `}
      >
        {label}
      </span>
      {sublabel && (
        <p className="text-xs text-neutral-dark/60 mt-0.5 truncate font-mono">
          {sublabel}
        </p>
      )}
    </div>
    <span
      className={`
      ml-4 tabular-nums font-mono
      ${highlight ? "text-lg font-bold text-primary" : ""}
      ${large ? "text-base font-bold text-text" : ""}
      ${!highlight && !large ? "font-semibold text-text" : ""}
    `}
    >
      ₹
      {typeof value === "number"
        ? value.toLocaleString("en-IN", { maximumFractionDigits: 2 })
        : "0"}
    </span>
  </div>
);

// ============================================
// COLLAPSIBLE SECTION COMPONENT
// ============================================
const CollapsibleSection = ({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-xl border border-neutral-light overflow-hidden shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-neutral-light/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="w-8 h-8 rounded-lg bg-neutral-light flex items-center justify-center">
              <Icon className="w-4 h-4 text-neutral-dark" />
            </div>
          )}
          <span className="font-semibold text-text text-sm">{title}</span>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-neutral-dark transition-transform duration-200 ${
            isOpen ? "rotate-90" : ""
          }`}
        />
      </button>
      <div
        className={`
        overflow-hidden transition-all duration-300 ease-out
        ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}
      `}
      >
        <div className="px-4 pb-4 pt-0">{children}</div>
      </div>
    </div>
  );
};

// ============================================
// RATE INFO CARD
// ============================================
const RateInfoCard = ({ rate }) => {
  if (!rate) return null;

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-primary via-primary-dark to-primary rounded-2xl p-5 text-white">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-secondary/15 rounded-full blur-2xl" />
      <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-white/5 rounded-full blur-xl" />

      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-medium text-white/70 uppercase tracking-wider">
            Applied Rate
          </span>
          <span className="text-xs px-2.5 py-1 bg-secondary/30 text-secondary-light rounded-full font-semibold border border-secondary/30">
            {rate.source}
          </span>
        </div>

        <div className="text-3xl font-bold text-white mb-4 tracking-tight">
          ₹{rate.applied?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          <span className="text-base font-normal text-white/60 ml-1">
            /{rate.per}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/10">
            <div className="flex items-center gap-1.5 text-success-light text-xs mb-1.5">
              <TrendingUp className="w-3.5 h-3.5" />
              <span className="font-medium">Sell Rate</span>
            </div>
            <span className="font-bold text-white text-lg">
              ₹{rate.sell?.toLocaleString("en-IN")}
            </span>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/10">
            <div className="flex items-center gap-1.5 text-danger-light text-xs mb-1.5">
              <TrendingDown className="w-3.5 h-3.5" />
              <span className="font-medium">Buy Rate</span>
            </div>
            <span className="font-bold text-white text-lg">
              ₹{rate.buy?.toLocaleString("en-IN")}
            </span>
          </div>
        </div>

        {rate.adjustments?.formula && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-xs text-white/60 font-mono bg-black/20 p-2.5 rounded-lg break-all">
              {rate.adjustments.formula}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// FORMULA CARD COMPONENT
// ============================================
const FormulaCard = ({ title, calculation, result }) => (
  <div className="bg-neutral-light rounded-xl p-4 border border-neutral-light">
    <p className="text-xs font-medium text-neutral-dark uppercase tracking-wide mb-2">
      {title}
    </p>
    <p className="font-mono text-xs text-text bg-white p-3 rounded-lg border border-neutral-light break-all leading-relaxed">
      {calculation}
    </p>
    {result !== undefined && (
      <p className="text-right font-bold text-primary mt-3 font-mono">
        = ₹{result.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
      </p>
    )}
  </div>
);

// ============================================
// SUMMARY STAT CARD
// ============================================
const SummaryCard = ({ label, value, highlight }) => (
  <div
    className={`
    rounded-xl p-4 transition-all duration-200
    ${
      highlight
        ? "bg-gradient-to-br from-primary to-primary-dark text-white shadow-lg shadow-primary/25"
        : "bg-white border border-neutral-light shadow-sm hover:shadow-md hover:border-primary/20"
    }
  `}
  >
    <p
      className={`text-xs font-medium uppercase tracking-wide mb-1 ${
        highlight ? "text-white/70" : "text-neutral-dark"
      }`}
    >
      {label}
    </p>
    <p
      className={`text-xl font-bold tabular-nums ${
        highlight ? "text-white" : "text-text"
      }`}
    >
      ₹{value?.toLocaleString("en-IN")}
    </p>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const PricingCalculator = ({ ruleId, onClose }) => {
  const dispatch = useDispatch();
  const resultsRef = useRef(null);
  const calculatedPricing = useSelector(selectCalculatedPricing);
  const loading = useSelector(selectCalculationLoading);
  const error = useSelector(selectCalculationError);

  const { user } = useSelector((state) => state.auth);
  const { branches, loadingList: loadingBranches } = useSelector(
    (state) => state.branch
  );

  useEffect(() => {
    dispatch(getBranchesThunk({ page: 1, limit: 100 }));
  }, [dispatch]);

  const getAccessibleBranches = () => {
    if (!user?.branchAccess) return branches || [];
    const { primaryBranch, secondaryBranches = [] } = user.branchAccess;
    const accessibleBranchCodes = [primaryBranch, ...secondaryBranches].filter(
      Boolean
    );
    if (user.role === "root" || user.role === "super_admin")
      return branches || [];
    return (branches || []).filter((branch) =>
      accessibleBranchCodes.includes(branch.code)
    );
  };

  const accessibleBranches = getAccessibleBranches();

  const [formData, setFormData] = useState({
    branchCode: user?.branchAccess?.primaryBranch || "",
    product: {
      type: "GOLD",
      purity: "22K",
      grossWeight: "",
      netWeight: "",
      stoneWeight: "",
      stoneValue: "",
      makingChargesType: "PER_GRAM",
    },
    customerType: "RETAIL",
    quantity: 1,
  });

  useEffect(() => {
    if (!formData.branchCode && user?.branchAccess?.primaryBranch) {
      setFormData((prev) => ({
        ...prev,
        branchCode: user.branchAccess.primaryBranch,
      }));
    }
  }, [user, formData.branchCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleReset = () => {
    setFormData({
      branchCode: user?.branchAccess?.primaryBranch || "",
      product: {
        type: "GOLD",
        purity: "22K",
        grossWeight: "",
        netWeight: "",
        stoneWeight: "",
        stoneValue: "",
        makingChargesType: "PER_GRAM",
      },
      customerType: "RETAIL",
      quantity: 1,
    });
  };

  const handleCalculate = async () => {
    try {
      await dispatch(
        calculatePricingThunk({
          ...formData,
          product: {
            ...formData.product,
            grossWeight: parseFloat(formData.product.grossWeight),
            netWeight: parseFloat(formData.product.netWeight),
            stoneWeight: parseFloat(formData.product.stoneWeight) || 0,
            stoneValue: parseFloat(formData.product.stoneValue) || 0,
          },
          quantity: parseInt(formData.quantity),
        })
      ).unwrap();

      setTimeout(() => {
        resultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } catch (err) {
      console.error("Calculation failed:", err);
    }
  };

  const isFormValid =
    formData.branchCode &&
    formData.product.grossWeight &&
    formData.product.netWeight;

  // Options
  const productTypes = [
    { value: "GOLD", label: "🥇  Gold" },
    { value: "SILVER", label: "🥈  Silver" },
    { value: "DIAMOND", label: "💎  Diamond" },
    { value: "PLATINUM", label: "⚪  Platinum" },
  ];

  const purityOptions = [
    { value: "24K", label: "24K (99.9%)" },
    { value: "22K", label: "22K (91.6%)" },
    { value: "18K", label: "18K (75%)" },
    { value: "14K", label: "14K (58.3%)" },
  ];

  const customerTypes = [
    { value: "RETAIL", label: "Retail Customer" },
    { value: "WHOLESALE", label: "Wholesale" },
    { value: "VIP", label: "VIP Customer" },
  ];

  const pricing = calculatedPricing;
  const breakdown = pricing?.breakdown;
  const calcDetails = pricing?.calculationDetails;
  const appliedRule = pricing?.applied?.pricingRule;
  const meta = pricing?.meta;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[99] p-0 sm:p-4">
      {/* Overlay click to close */}
      <div className="absolute inset-0" onClick={onClose} />

      <div
        className="relative bg-neutral-light sm:bg-white sm:rounded-2xl shadow-2xl w-full h-full sm:h-auto sm:max-w-2xl sm:max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          animation: "modalIn 0.2s ease-out",
        }}
      >
        <style>{`
          @keyframes modalIn {
            from {
              opacity: 0;
              transform: scale(0.95) translateY(10px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          @keyframes fadeSlideIn {
            from {
              opacity: 0;
              transform: translateY(16px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fade-slide-in {
            animation: fadeSlideIn 0.3s ease-out;
          }
        `}</style>

        {/* ========== HEADER ========== */}
        <div className="relative bg-white border-b border-neutral-light px-5 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center shadow-lg shadow-primary/25">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-text">
                  Pricing Calculator
                </h2>
                <p className="text-xs text-neutral-dark hidden sm:block">
                  Calculate product pricing with live rates
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-xl text-neutral-dark hover:text-text hover:bg-neutral-light transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ========== SCROLLABLE CONTENT ========== */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-5 space-y-5">
            {/* ========== INPUT FORM ========== */}
            <div className="bg-white rounded-xl p-5 border border-neutral-light shadow-sm">
              {/* Branch & Customer Section */}
              <SectionDivider icon={Building2} title="Branch & Customer" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
                <div>
                  <label className="block text-xs font-medium text-neutral-dark uppercase tracking-wide mb-2">
                    Branch <span className="text-secondary">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="branchCode"
                      value={formData.branchCode}
                      onChange={handleChange}
                      disabled={
                        loadingBranches || accessibleBranches.length <= 1
                      }
                      className="
                        w-full appearance-none px-4 py-3 pr-10 text-sm font-medium
                        bg-white border-2 border-neutral-light rounded-xl text-text
                        transition-all duration-200 ease-out
                        focus:border-primary focus:shadow-[0_0_0_3px_rgba(22,101,52,0.1)] focus:outline-none
                        hover:border-primary/30
                        disabled:bg-neutral-light disabled:cursor-not-allowed disabled:text-neutral-dark
                      "
                    >
                      {loadingBranches ? (
                        <option value="">Loading...</option>
                      ) : accessibleBranches.length === 0 ? (
                        <option value="">No branches</option>
                      ) : (
                        <>
                          <option value="">Select Branch</option>
                          {accessibleBranches.map((branch) => (
                            <option key={branch.code} value={branch.code}>
                              {branch.name} ({branch.code})
                              {branch.code ===
                                user?.branchAccess?.primaryBranch && " ★"}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-dark pointer-events-none" />
                  </div>
                  {formData.branchCode &&
                    (() => {
                      const selectedBranch = accessibleBranches.find(
                        (b) => b.code === formData.branchCode
                      );
                      return selectedBranch?.rateMode ? (
                        <span
                          className={`
                        inline-flex items-center mt-2 px-2 py-0.5 rounded-md text-xs font-semibold
                        ${
                          selectedBranch.rateMode === "LIVE"
                            ? "bg-success/10 text-success-dark"
                            : ""
                        }
                        ${
                          selectedBranch.rateMode === "INHERIT"
                            ? "bg-secondary/10 text-secondary-dark"
                            : ""
                        }
                        ${
                          selectedBranch.rateMode !== "LIVE" &&
                          selectedBranch.rateMode !== "INHERIT"
                            ? "bg-neutral-light text-neutral-dark"
                            : ""
                        }
                      `}
                        >
                          {selectedBranch.rateMode} Rate
                        </span>
                      ) : null;
                    })()}
                </div>
                <CustomSelect
                  label="Customer Type"
                  name="customerType"
                  value={formData.customerType}
                  onChange={handleChange}
                  options={customerTypes}
                />
              </div>

              {/* Product Details Section */}
              <SectionDivider icon={Sparkles} title="Product Details" />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-2">
                <CustomSelect
                  label="Type"
                  name="product.type"
                  value={formData.product.type}
                  onChange={handleChange}
                  options={productTypes}
                  required
                />
                <CustomSelect
                  label="Purity"
                  name="product.purity"
                  value={formData.product.purity}
                  onChange={handleChange}
                  options={purityOptions}
                  required
                />
                <InputField
                  label="Quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  type="number"
                  min="1"
                  placeholder="1"
                />
              </div>

              {/* Weight Details Section */}
              <SectionDivider icon={Scale} title="Weight Details" />
              <div className="grid grid-cols-2 gap-4 mb-2">
                <InputField
                  label="Gross Weight"
                  name="product.grossWeight"
                  value={formData.product.grossWeight}
                  onChange={handleChange}
                  type="number"
                  step="0.001"
                  placeholder="0.000"
                  suffix="gm"
                  required
                />
                <InputField
                  label="Net Weight"
                  name="product.netWeight"
                  value={formData.product.netWeight}
                  onChange={handleChange}
                  type="number"
                  step="0.001"
                  placeholder="0.000"
                  suffix="gm"
                  required
                />
              </div>

              {/* Stone Details Section */}
              <SectionDivider icon={Gem} title="Stone Details (Optional)" />
              <div className="grid grid-cols-2 gap-4 mb-6">
                <InputField
                  label="Stone Weight"
                  name="product.stoneWeight"
                  value={formData.product.stoneWeight}
                  onChange={handleChange}
                  type="number"
                  step="0.001"
                  placeholder="0.000"
                  suffix="gm"
                />
                <InputField
                  label="Stone Value"
                  name="product.stoneValue"
                  value={formData.product.stoneValue}
                  onChange={handleChange}
                  type="number"
                  placeholder="0"
                  suffix="₹"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleReset}
                  className="
                    flex items-center justify-center gap-2 px-4 py-3 
                    text-neutral-dark bg-neutral-light rounded-xl font-medium text-sm
                    hover:bg-neutral-light/80 active:scale-[0.98] transition-all
                    border border-transparent hover:border-neutral-dark/10
                  "
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="hidden sm:inline">Reset</span>
                </button>
                <button
                  onClick={handleCalculate}
                  disabled={loading || !isFormValid}
                  className="
                    flex-1 flex items-center justify-center gap-2 px-6 py-3 
                    bg-gradient-to-r from-primary to-primary-dark text-white 
                    rounded-xl font-semibold text-sm
                    shadow-lg shadow-primary/25 
                    hover:shadow-xl hover:shadow-primary/30 hover:brightness-110
                    active:scale-[0.98]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:brightness-100
                    transition-all duration-200
                  "
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Calculating...</span>
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4" />
                      <span>Calculate Price</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* ========== ERROR STATE ========== */}
            {error && (
              <div className="bg-danger/10 border border-danger/20 rounded-xl p-4 flex items-start gap-3">
                <div className="w-8 h-8 bg-danger/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-4 h-4 text-danger" />
                </div>
                <div>
                  <p className="font-semibold text-danger-dark text-sm">
                    Calculation Error
                  </p>
                  <p className="text-sm text-danger mt-0.5">{error}</p>
                </div>
              </div>
            )}

            {/* ========== RESULTS SECTION ========== */}
            <div ref={resultsRef}>
              {pricing ? (
                <div className="space-y-4 animate-fade-slide-in">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <SummaryCard
                      label="Metal Value"
                      value={breakdown?.metalValue}
                    />
                    <SummaryCard
                      label="Total Charges"
                      value={breakdown?.charges?.total}
                    />
                    <SummaryCard
                      label={`GST (${breakdown?.gst?.rate}%)`}
                      value={breakdown?.gst?.amount}
                    />
                    <SummaryCard
                      label="Net Payable"
                      value={breakdown?.netPayable}
                      highlight
                    />
                  </div>

                  {/* Rate Information */}
                  <RateInfoCard rate={pricing?.rate} />

                  {/* Price Breakdown */}
                  <div className="bg-white rounded-xl border border-neutral-light p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <Receipt className="w-5 h-5 text-primary" />
                      <h4 className="font-bold text-text">Price Breakdown</h4>
                    </div>

                    <PriceRow
                      label="Metal Value"
                      value={breakdown?.metalValue}
                      sublabel={`${
                        pricing?.weight?.net || formData.product.netWeight
                      }gm × ₹${pricing?.rate?.applied?.toLocaleString(
                        "en-IN"
                      )}/gm`}
                      large
                    />

                    {/* Charges */}
                    <div className="my-4">
                      <p className="text-xs font-semibold text-neutral-dark uppercase tracking-wider mb-2">
                        Charges
                      </p>
                      <PriceRow
                        label="Making Charges"
                        value={breakdown?.charges?.making}
                        sublabel={
                          calcDetails?.appliedRates?.makingCharge
                            ? `${calcDetails.appliedRates.makingCharge.value}% of metal`
                            : undefined
                        }
                        indent
                      />
                      {breakdown?.charges?.wastage > 0 && (
                        <PriceRow
                          label="Wastage"
                          value={breakdown?.charges?.wastage}
                          sublabel={
                            calcDetails?.appliedRates?.wastagePercent
                              ? `${calcDetails.appliedRates.wastagePercent}%`
                              : undefined
                          }
                          indent
                        />
                      )}
                      {breakdown?.charges?.hallmark > 0 && (
                        <PriceRow
                          label="Hallmark"
                          value={breakdown?.charges?.hallmark}
                          indent
                        />
                      )}
                      {breakdown?.charges?.additional > 0 && (
                        <PriceRow
                          label="Additional"
                          value={breakdown?.charges?.additional}
                          sublabel={
                            calcDetails?.formulas?.charges?.additional?.[0]
                              ?.name
                          }
                          indent
                        />
                      )}
                    </div>

                    {breakdown?.stoneCharges > 0 && (
                      <PriceRow
                        label="Stone Charges"
                        value={breakdown?.stoneCharges}
                      />
                    )}

                    {breakdown?.discount > 0 && (
                      <PriceRow label="Discount" value={-breakdown?.discount} />
                    )}

                    <div className="my-3 border-t border-neutral-light" />

                    <PriceRow
                      label="Taxable Amount"
                      value={breakdown?.taxableAmount}
                    />

                    <PriceRow
                      label={`GST @ ${breakdown?.gst?.rate}%`}
                      value={breakdown?.gst?.amount}
                      sublabel={`Incl: ${[
                        breakdown?.gst?.config?.includesMaking && "Making",
                        breakdown?.gst?.config?.includesWastage && "Wastage",
                        breakdown?.gst?.config?.includesStones && "Stones",
                      ]
                        .filter(Boolean)
                        .join(", ")}`}
                    />

                    <div className="my-4 border-t-2 border-dashed border-neutral-light" />

                    <PriceRow
                      label="Final Amount"
                      value={breakdown?.finalAmount}
                      large
                    />
                    <PriceRow
                      label="Net Payable"
                      value={breakdown?.netPayable}
                      highlight
                    />
                  </div>

                  {/* Calculation Details */}
                  <CollapsibleSection
                    title="Calculation Formulas"
                    icon={FileText}
                  >
                    <div className="space-y-3">
                      {calcDetails?.formulas?.metalValue && (
                        <FormulaCard
                          title="Metal Value"
                          calculation={
                            calcDetails.formulas.metalValue.calculation
                          }
                          result={calcDetails.formulas.metalValue.result}
                        />
                      )}
                      {calcDetails?.formulas?.charges?.making && (
                        <FormulaCard
                          title="Making Charges"
                          calculation={
                            calcDetails.formulas.charges.making.calculation
                          }
                          result={
                            calcDetails.formulas.charges.making.breakdown
                              ?.result
                          }
                        />
                      )}
                      {calcDetails?.formulas?.charges?.wastage && (
                        <FormulaCard
                          title="Wastage"
                          calculation={
                            calcDetails.formulas.charges.wastage.calculation
                          }
                          result={
                            calcDetails.formulas.charges.wastage.breakdown
                              ?.result
                          }
                        />
                      )}
                      {calcDetails?.formulas?.gst && (
                        <FormulaCard
                          title="GST"
                          calculation={calcDetails.formulas.gst.calculation}
                          result={calcDetails.formulas.gst.result}
                        />
                      )}
                    </div>
                  </CollapsibleSection>

                  {/* Applied Rule */}
                  {appliedRule && (
                    <div className="bg-success/10 border border-success/20 rounded-xl p-4 flex items-start gap-3">
                      <div className="w-10 h-10 bg-success/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-success" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-success-dark text-sm">
                          Applied Rule
                        </h4>
                        <div className="mt-2 text-sm space-y-1">
                          <p className="text-success-dark">
                            <span className="text-success">Name:</span>{" "}
                            <span className="font-semibold">
                              {appliedRule.name}
                            </span>
                          </p>
                          <p className="text-success-dark">
                            <span className="text-success">Priority:</span>{" "}
                            <span className="font-semibold">
                              {appliedRule.priority}
                            </span>
                            <span className="text-success ml-3">Source:</span>{" "}
                            <span className="font-semibold">
                              {appliedRule.source}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Meta Info */}
                  {meta && (
                    <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-dark bg-white rounded-xl p-4 border border-neutral-light">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-neutral-dark/60" />
                        <span>
                          {new Date(meta.calculatedAt).toLocaleString("en-IN", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-neutral-dark/60" />
                        <span>{meta.branchCode}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5 text-neutral-dark/60" />
                        <span>{meta.customerType}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Zap className="w-3.5 h-3.5 text-neutral-dark/60" />
                        <span>
                          Valid: {Math.round(meta.validFor / 60000)}min
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Empty State */
                !error && (
                  <div className="hidden sm:block bg-white rounded-xl border-2 border-dashed border-primary/20 p-10 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center border border-primary/10">
                      <Receipt className="w-7 h-7 text-primary" />
                    </div>
                    <h4 className="font-bold text-text mb-2">
                      Ready to Calculate
                    </h4>
                    <p className="text-sm text-neutral-dark max-w-xs mx-auto">
                      Fill in the product details above and click Calculate to
                      see the complete pricing breakdown
                    </p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* ========== FOOTER ========== */}
        <div className="px-5 py-4 border-t border-neutral-light bg-white flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-neutral-dark text-center sm:text-left">
              Prices based on current market rates & applied rules
            </p>
            <button
              onClick={onClose}
              className="
                w-full sm:w-auto px-5 py-2.5 
                text-neutral-dark bg-neutral-light rounded-xl font-medium text-sm
                hover:bg-neutral-light/80 active:scale-[0.98] transition-all
                border border-transparent hover:border-neutral-dark/10
              "
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;
